import React, { useCallback, useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import type { CoachAvailability } from '@court-sync/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui';
import { coachService } from '../../services/coach';
import { groupAvailabilityByDay, formatTimeRange } from '../../utils/coachAvailabilityUtils';
import { CoachAvailabilityEditor } from '../coach/CoachAvailabilityEditor';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface Props {
  coachId: string;
  academyId: string;
  coachName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function CoachAvailabilityModal({ coachId, academyId, coachName, isOpen, onClose }: Props) {
  const [slots, setSlots] = useState<CoachAvailability[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const refreshSlots = useCallback(async () => {
    try {
      const data = await coachService.getAvailability(coachId, academyId);
      setSlots(data);
    } catch {
      /* keep existing list on silent refresh failure */
    }
  }, [coachId, academyId]);

  useEffect(() => {
    if (!isOpen) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setEditOpen(false);
      /* eslint-enable react-hooks/set-state-in-effect */
      return undefined;
    }
    let mounted = true;
    /* eslint-disable react-hooks/set-state-in-effect */
    setLoading(true);
    setError(null);
    /* eslint-enable react-hooks/set-state-in-effect */
    coachService
      .getAvailability(coachId, academyId)
      .then((data) => { if (mounted) setSlots(data); })
      .catch(() => { if (mounted) setError('Failed to load availability. Please try again.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [isOpen, coachId, academyId]);

  const availabilityByDay = groupAvailabilityByDay(slots);
  const hasAnyAvailability = availabilityByDay.size > 0;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              {coachName}&apos;s Availability
            </DialogTitle>
            <DialogDescription>
              Weekly recurring availability schedule
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {loading && (
              <div className="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" />
                Loading availability...
              </div>
            )}

            {!loading && error && (
              <p className="text-sm text-red-600 dark:text-red-400 text-center py-4">{error}</p>
            )}

            {!loading && !error && !hasAnyAvailability && slots.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No availability has been configured yet.
              </p>
            )}

            {!loading && !error && (
              <ul className="space-y-2">
                {DAY_NAMES.map((dayName, dayIndex) => {
                  const ranges = availabilityByDay.get(dayIndex);
                  return (
                    <li key={dayIndex} className="flex items-start justify-between text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300 w-28 shrink-0">
                        {dayName}
                      </span>
                      {ranges && ranges.length > 0 ? (
                        <span className="text-gray-600 dark:text-gray-400 text-right">
                          {ranges.map((r, i) => (
                            <span key={i} className="block">
                              {formatTimeRange(r.startTime, r.endTime)}
                            </span>
                          ))}
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-700 pt-3 mt-1">
            <Button
              type="button"
              className="w-full"
              onClick={() => setEditOpen(true)}
            >
              Set availability
            </Button>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              You can update this coach&apos;s weekly hours and date exceptions here, or they can edit from their coach dashboard.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Set availability for {coachName}</DialogTitle>
            <DialogDescription>
              Adjust weekly hours and exceptions. Changes apply immediately after you save.
            </DialogDescription>
          </DialogHeader>
          <CoachAvailabilityEditor
            coachId={coachId}
            academyId={academyId}
            layout="embedded"
            audience="ownerManagingCoach"
            onSaved={(detail) => {
              void refreshSlots();
              if (detail.source === 'weekly') {
                setEditOpen(false);
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
